import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, filter, interval, map, of, switchMap, takeWhile, tap } from 'rxjs';

import {
  AccountInfo,
  AdminLevel,
  EnvironmentName,
  ProjectRoleName,
  UserInfo,
} from '@blaze-shared/api-contracts-domain';
import {
  type AppState,
  appStateActions,
  blazeActions,
  type BlazeProjectUserData,
  generalSettingsSelectors,
  type NewVersionAction,
} from '@blaze-shared/app-state-data-access';
import { UsersApiService } from '@blaze-shared/auth-data-access';
import { hasAdminLevel, hasProjectRole } from '@blaze-shared/auth-domain';
import {
  DEV_SUBDOMAIN_OVERRIDE,
  LocalStorageService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import { type VarUser } from '@blaze-shared/schema-domain';
import { VarAccount } from '@blaze-shared/schema-domain';
import { IconsService } from '@blaze-shared/theming-data-access';

@Injectable()
export class BlazeEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly devSubdomainOverride = inject(DEV_SUBDOMAIN_OVERRIDE);
  private readonly iconsService = inject(IconsService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly usersApiService = inject(UsersApiService);

  private userActivity = false;

  requestAccountAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appStateActions.hydrateFromLocalStorage),
      filter(({ publishedMode }) => !publishedMode),
      map(() => blazeActions.loadAccountAssets()),
    ),
  );

  /**
   * Account icons are loaded at a different moment depending on runtime mode; the fonts that
   * travel with them are `shared/theming`'s to load.
   */
  loadAccountIcons$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.loadAccountAssets),
        // Avoid double api call. Loading of icons on login pages is not required.
        filter(() => !urlUtils.currentUrlIsLoginRelated()),
        tap(() => this.iconsService.getAccountIconsAndRegisterOnce().subscribe()),
      ),
    { dispatch: false },
  );

  /**
   * Ping server every 1 minute to determine:
   * 1. Whether the token has expired when authentication is required. If so, automatically log off the user.
   * 2. Whether a new version of the published project is available.
   * 3. Whether page should be reloaded because new version has been published.
   */
  checkVersionAndAutoLogOffWhenUserInactive$ = createEffect(() =>
    interval(1 * 60 * 1000).pipe(
      takeWhile(() => urlUtils.currentUrlRequiresAuthentication() || urlUtils.isPublishedMode()),
      concatLatestFrom(() => this.store.select(generalSettingsSelectors.selectGeneralSettings)),
      filter(() => !urlUtils.currentUrlIsLoginRelated()),
      switchMap(([_intervalCount, generalSettings]) => {
        const userActivity = this.userActivity;
        this.userActivity = false;
        if (
          !urlUtils.isPublishedMode() ||
          (!this.devSubdomainOverride && urlUtils.isLocalHostUrl())
        ) {
          // If dev hasn't specified a subdomain to use when on localhost, do not send project name and version,
          // otherwise server will error out not being able to find appropriate project.
          return this.usersApiService.postPing(undefined, undefined, userActivity);
        }
        return this.usersApiService.postPing(
          generalSettings.name,
          generalSettings.version,
          userActivity,
        );
      }),
      filter((pingResp) => {
        return (
          !!pingResp?.newVersionAvailable && (!!pingResp?.notifyAppUsers || pingResp?.forceReload)
        );
      }),
      map((pingResp) => {
        const newVersionAction: NewVersionAction = pingResp?.forceReload
          ? 'force-reload'
          : 'ask-to-reload';
        return appStateActions.setNewVersionAction({ newVersionAction });
      }),
    ),
  );

  loadUserAndHydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.loadUserAndHydrate),
      switchMap(() => {
        // When opening a published version of an app with login, it's possible that local
        // storage is empty or has previous login info stored in it. To avoid loading incorrect
        // user, we fetch up-to-date information about the user and only then proceed with loading
        // the project.
        return urlUtils.currentUrlRequiresAuthentication()
          ? this.usersApiService.getMe().pipe(catchError(() => of(null)))
          : of(null);
      }),
      map((loginResponse) => {
        const user = loginResponse?.user ?? this.localStorageService.get<UserInfo>(StorageKey.User);
        const account = this.localStorageService.get<AccountInfo>(StorageKey.Account);
        const projectUserData = this.localStorageService.get<BlazeProjectUserData>(
          StorageKey.ProjectUserData,
        );

        const url = new URL(globalThis.location.href);
        const pathname = url.pathname;

        const evnFromQuery: string | null = url.searchParams.get('environment');
        const publishedMode = urlUtils.isPublishedMode(pathname);
        let environmentFromQuery: EnvironmentName | undefined;
        if (evnFromQuery === 'Production' || evnFromQuery === 'Development') {
          environmentFromQuery = evnFromQuery;
        }

        // For preview, we want to make sure we use the preview user from the editor as VarUser.
        let varAccount;
        let varUser;
        let environmentName: EnvironmentName | undefined | null;

        // We can't use ActivatedRoute here because query params are set at the time of execution of this code.
        let projectId = new URL(globalThis.location.href).searchParams.get('projectId');
        if (!projectId && urlUtils.isEditorMode()) {
          // fromRoot selector for projectId is not yet available at this point. Parse projectId from /projects/ url.
          projectId = pathname.split('/')?.[2];
        }

        if (environmentFromQuery) {
          // Query environment overrides environment read from Session. This is useful for testing and for printing to PDF.
          environmentName = environmentFromQuery;
        } else {
          const redirectPathnameFromUrl = url.searchParams.get('redirect') || undefined;

          // For published project, always default to Production environment.
          // For preview mode where projectId is specified in the URL, restore data environment information from session storage.
          if (
            urlUtils.isPublishedMode(redirectPathnameFromUrl) &&
            !urlUtils.isPublishedStagingMode(this.devSubdomainOverride, redirectPathnameFromUrl)
          ) {
            environmentName = 'Production';
          } else if (
            urlUtils.isPublishedMode(redirectPathnameFromUrl) &&
            urlUtils.isPublishedStagingMode(this.devSubdomainOverride, redirectPathnameFromUrl)
          ) {
            // For published to staging, default environment is Development.
            environmentName = 'Development';
          } else if (
            projectId &&
            (hasAdminLevel(user, AdminLevel.Admin) ||
              hasProjectRole(user, ProjectRoleName.ProjectAdmin, projectId))
          ) {
            environmentName = this.localStorageService.getForProject(
              projectId,
              StorageKey.EnvironmentName,
              sessionStorage,
            );
          }
        }
        // Default to Development environment if no proper environment has been found.
        if (!environmentName) {
          environmentName = 'Development';
        }

        if (projectId && urlUtils.isPreviewMode()) {
          varAccount =
            this.localStorageService.getForProject<VarAccount>(projectId, StorageKey.VarAccount) ||
            undefined;
          varUser =
            this.localStorageService.getForProject<VarUser>(projectId, StorageKey.VarUser) ||
            undefined;
        }

        return appStateActions.hydrateFromLocalStorage({
          account,
          environmentName,
          projectUserData,
          publishedMode,
          user,
          varAccount,
          varUser,
        });
      }),
    ),
  );

  /**
   * Hydrates store using data stored in localStorage.
   */
  ngrxOnInitEffects(): Action {
    this.document.addEventListener(
      'keydown',
      () => {
        this.userActivity = true;
      },
      { capture: true },
    );
    this.document.addEventListener(
      'mousedown',
      () => {
        this.userActivity = true;
      },
      { capture: true },
    );

    return blazeActions.loadUserAndHydrate();
  }
}
